import * as React from 'react';
import { cn } from '../../helpers/shadcnUtils.helper';

interface SearchProps extends React.InputHTMLAttributes<HTMLInputElement> {
  label: string; // Floating label text
  error?: string; // Optional error message
  icon?: React.ReactNode; // Icon element
  actionButton?: React.ReactNode; // Generic action button
  onActionClick?: () => void; // Callback for action button click
}

const SearchTest = React.forwardRef<HTMLInputElement, SearchProps>(
  (
    {
      label,
      error,
      id,
      className,
      icon,
      actionButton,
      onActionClick,
      value,
      onChange,
      onBlur,
      type = 'search', // Default to search
      placeholder = ' ', // Needed for floating label to work with peer-placeholder-shown
      ...props
    },
    ref,
  ) => {
    // Fallback ID if none is provided
    const fallbackId = React.useId();
    const inputId = id || fallbackId;

    // Handle action button click
    const handleActionClick = (
      e: React.MouseEvent<HTMLButtonElement | HTMLSpanElement>,
    ) => {
      e.preventDefault();
      onActionClick?.();
    };

    return (
      <div className="w-full">
        <div className="relative w-full">
          {/* Icon */}
          {icon && (
            <span className="absolute text-gray-400 -translate-y-1/2 left-3 top-1/2">
              {icon}
            </span>
          )}

          <input
            ref={ref}
            id={inputId}
            value={value}
            onChange={onChange}
            onBlur={onBlur}
            type={type}
            placeholder={placeholder}
            // Accessibility for errors
            aria-invalid={!!error}
            aria-describedby={error ? `${inputId}-error` : undefined}
            className={cn(
              'peer block h-12 w-full rounded-xl bg-neutral-98 px-3 pt-4 pb-1 text-base',
              'hover:bg-blue-95 focus:border focus:border-primary focus-visible:outline-none',
              'disabled:cursor-not-allowed disabled:opacity-50',
              icon ? 'pl-12' : 'pl-3',
              actionButton ? 'pr-12' : 'pr-3',
              error ? 'border border-red-500 focus:border-red-500' : '',
              className,
            )}
            {...props}
          />

          {/* Floating Label */}
          <label
            htmlFor={inputId}
            className={cn(
              'pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 transform text-base text-neutral-40',
              'origin-left transition-all duration-300',
              // Default (placeholder-shown) state
              'peer-placeholder-shown:top-1/2 peer-placeholder-shown:-translate-y-1/2 peer-placeholder-shown:scale-100',
              // Focused or has value
              'peer-focus:!top-1 peer-focus:scale-75 peer-focus:translate-y-0',
              // If input already has a value
              value ? '!top-1 !scale-75 translate-y-0' : '',
              icon ? 'left-12' : 'left-3',
            )}
          >
            {label}
          </label>

          {/* Action Button */}
          {actionButton && (
            <span
              className="absolute -translate-y-1/2 cursor-pointer right-3 top-1/2"
              onClick={handleActionClick}
            >
              {actionButton}
            </span>
          )}
        </div>

        {/* Optional Error Message */}
        {error && (
          <p
            id={`${inputId}-error`}
            className="mt-1 text-sm text-red-600"
            role="alert"
          >
            {error}
          </p>
        )}
      </div>
    );
  },
);

SearchTest.displayName = 'SearchTest';

export { SearchTest };
